import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { errorMessage } from "../utils/errorMessage";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  RotateCcw,
  ShieldOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Lockdown F (29Apr2026) — single Admin Dashboard.
 *
 * Tiles are powered by GET /api/admin/dashboard/summary. Click-through opens
 * the matching audit endpoint in a tabular drawer. The Soft-deleted tile
 * supports per-row Restore via POST /api/admin/restore/{collection}/{doc_id}.
 *
 * Access: directors only (BM-H625), as the server decides through
 * /api/user-role. Anyone else sees a "Directors only" panel, no silent
 * redirect.
 */
const STATUS_STYLES = {
  ok:   { dot: "bg-emerald-500", label: "Healthy",     text: "text-emerald-700" },
  warn: { dot: "bg-amber-500",   label: "Attention",   text: "text-amber-700"  },
  fail: { dot: "bg-red-500",     label: "Action req.", text: "text-red-700"    },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.fail;
  return (
    <div className="flex items-center gap-1.5 text-xs font-medium">
      <span className={`w-2 h-2 rounded-full ${s.dot}`} aria-hidden />
      <span className={s.text}>{s.label}</span>
    </div>
  );
}

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminDashboard({ embedded = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tiles, setTiles] = useState([]);
  const [runAt, setRunAt] = useState("");
  const [error, setError] = useState("");
  const [openTile, setOpenTile] = useState(null);

  const userId = sessionStorage.getItem("bonsai_user_id") || "";
  // BM-H625 (26 Sep 2026): directors only, as the server decides. This used
  // to compare the stored login id (bonsai_<id>) with a list of admin
  // EMAILS, which never matched, so everyone was refused; and that list
  // named the admin team rather than the directors.
  const [allowed, setAllowed] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/user-role`, { headers: { "X-Bonsai-User-Id": userId } })
      .then((res) => { if (!cancelled) setAllowed(Boolean(res.data?.is_director)); })
      .catch(() => { if (!cancelled) setAllowed(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/api/admin/dashboard/summary`, {
        headers: { "X-Bonsai-User-Id": userId },
      });
      setTiles(res.data.tiles || []);
      setRunAt(res.data.run_at || "");
    } catch (e) {
      const msg = errorMessage(e, "Failed to load");
      setError(msg);
      toast.error(`Dashboard load failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  if (allowed === null) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] p-6" data-testid="admin-dashboard-checking">
        <p className="text-sm text-slate-600 max-w-3xl mx-auto mt-12 text-center">Checking access…</p>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] p-6" data-testid="admin-dashboard-403">
        <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-md p-8 mt-12 text-center">
          <ShieldOff className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-slate-800 mb-1">
            Directors only
          </h1>
          <p className="text-sm text-slate-600 mb-4">
            This dashboard is restricted to the firm's directors.
          </p>
          <Button size="sm" variant="outline" onClick={() => navigate("/")}>
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "p-4" : "min-h-screen bg-[#F5F5F0] p-6"} data-testid="admin-dashboard-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-teal">{embedded ? "Data health" : "Admin Dashboard"}</h1>
            <p className="text-sm text-slate-600">
              Firm-wide health audits, soft-deleted items, and restore actions.
              {runAt && (
                <span className="ml-2 text-slate-400">
                  Data as of {formatTimestamp(runAt)}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* BM-H247 F-04: discoverable link to Bonsai Mail Failure Console */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/admin/bonsai-mail/failures")}
              data-testid="open-bonsai-mail-failures"
            >
              Bonsai Mail failures
            </Button>
            <Button
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="dashboard-refresh-btn"
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              )}
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div
            className="mb-4 p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700 flex items-start gap-2"
            data-testid="dashboard-error"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="dashboard-tile-grid"
        >
          {tiles.map((t) => (
            <DashboardTile
              key={t.key}
              tile={t}
              onOpen={() => setOpenTile(t)}
            />
          ))}
          {tiles.length === 0 && !loading && !error && (
            <div className="col-span-full text-center text-slate-400 py-12 text-sm">
              No tiles to show.
            </div>
          )}
        </div>

        {openTile && (
          <TileDrawer
            tile={openTile}
            userId={userId}
            onClose={() => setOpenTile(null)}
            onAfterAction={load}
          />
        )}
      </div>
    </div>
  );
}

function DashboardTile({ tile, onOpen }) {
  const showCount = tile.count !== null && tile.count !== undefined;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left bg-white border border-slate-200 rounded-md shadow-sm p-4 hover:border-teal hover:shadow-md transition"
      data-testid={`tile-${tile.key}`}
    >
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-800">{tile.title}</h3>
        <StatusPill status={tile.status} />
      </div>
      <div className="text-3xl font-light text-slate-900 leading-none mb-1">
        {showCount ? tile.count : "—"}
      </div>
      <p className="text-xs text-slate-500 mb-2">{tile.description}</p>
      {tile.error && (
        <div className="text-xs text-red-600 mt-1" data-testid={`tile-error-${tile.key}`}>
          Error: {tile.error}
        </div>
      )}
      <div className="text-[11px] text-slate-400 mt-2">
        as of {formatTimestamp(tile.data_as_of)}
      </div>
    </button>
  );
}

function TileDrawer({ tile, userId, onClose, onAfterAction }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [restoringKey, setRestoringKey] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}${tile.path}`, {
        headers: { "X-Bonsai-User-Id": userId },
      });
      setData(res.data);
    } catch (e) {
      const msg = errorMessage(e, "Load failed");
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }, [tile.path, userId]);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (collection, docId) => {
    if (!collection || !docId) {
      toast.error("Missing identifier — cannot restore.");
      return;
    }
    const key = `${collection}:${docId}`;
    setRestoringKey(key);
    try {
      await axios.post(
        `${API}/api/admin/restore/${encodeURIComponent(collection)}/${encodeURIComponent(docId)}`,
        { confirm: true },
        { headers: { "X-Bonsai-User-Id": userId } }
      );
      toast.success(`Restored ${collection}:${docId}`);
      await load();
      onAfterAction?.();
    } catch (e) {
      const msg = errorMessage(e, "Restore failed");
      toast.error(`Restore failed: ${msg}`);
    } finally {
      setRestoringKey("");
    }
  };

  const handleNavigateOut = () => {
    if (tile.key === "ob_link_health") {
      navigate("/admin/onboarding-link-health");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-40 flex justify-end"
      onClick={onClose}
      data-testid={`tile-drawer-${tile.key}`}
    >
      <div
        className="w-full max-w-3xl h-full bg-white shadow-xl overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">{tile.title}</h2>
            <p className="text-xs text-slate-500 font-mono mt-1">{tile.path}</p>
          </div>
          <Button size="sm" variant="outline" onClick={onClose}>Close</Button>
        </div>

        {tile.key === "ob_link_health" && (
          <div className="mb-4">
            <Button size="sm" variant="outline" onClick={handleNavigateOut}>
              Open full OB Link Health page
            </Button>
          </div>
        )}

        {loading && (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {err && (
          <div className="p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 inline mr-1" /> {err}
          </div>
        )}

        {!loading && !err && data && (
          tile.key === "soft_deleted" ? (
            <SoftDeletedView
              data={data}
              onRestore={handleRestore}
              restoringKey={restoringKey}
            />
          ) : (
            <RawJsonView data={data} />
          )
        )}
      </div>
    </div>
  );
}

function SoftDeletedView({ data, onRestore, restoringKey }) {
  const sections = data?.sections || [];
  if (data.total === 0) {
    return (
      <div className="text-sm text-slate-500 flex items-center gap-2 py-6">
        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
        No soft-deleted items in any whitelisted collection.
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {sections.map((section) => (
        <div key={section.collection}>
          <h3 className="text-sm font-semibold text-slate-800 mb-2">
            {section.collection}{" "}
            <span className="text-slate-400 font-normal">({section.count})</span>
          </h3>
          {section.rows.length === 0 ? (
            <div className="text-xs text-slate-400">No rows.</div>
          ) : (
            <table
              className="w-full text-xs border border-slate-200 rounded-md overflow-hidden"
              data-testid={`soft-deleted-table-${section.collection}`}
            >
              <thead className="bg-slate-100 text-slate-700 uppercase">
                <tr>
                  <th className="px-2 py-1.5 text-left">Identifier</th>
                  <th className="px-2 py-1.5 text-left">Label</th>
                  <th className="px-2 py-1.5 text-left">Deleted at</th>
                  <th className="px-2 py-1.5 text-left">Reason</th>
                  <th className="px-2 py-1.5 text-left"></th>
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row) => {
                  const docId =
                    row.id ||
                    row.matter_reference ||
                    row.provisional_id ||
                    "";
                  const label =
                    row.primary_label ||
                    row.client_name ||
                    row?.primary_client?.full_name ||
                    row.title ||
                    row.matter_reference ||
                    "—";
                  const reason =
                    row.deletion_reason ||
                    row.deleted_reason ||
                    row.delete_reason ||
                    "—";
                  const key = `${section.collection}:${docId}`;
                  return (
                    <tr
                      key={key}
                      className="border-t border-slate-100"
                      data-testid={`soft-deleted-row-${docId}`}
                    >
                      <td className="px-2 py-1.5 font-mono">{docId || "—"}</td>
                      <td className="px-2 py-1.5">{label}</td>
                      <td className="px-2 py-1.5 text-slate-500">
                        {formatTimestamp(row.deleted_at)}
                      </td>
                      <td className="px-2 py-1.5 text-slate-500">{reason}</td>
                      <td className="px-2 py-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={restoringKey === key || !docId}
                          onClick={() => onRestore(section.collection, docId)}
                          data-testid={`restore-btn-${docId}`}
                        >
                          {restoringKey === key ? (
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          ) : (
                            <RotateCcw className="w-3 h-3 mr-1" />
                          )}
                          Restore
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

function RawJsonView({ data }) {
  return (
    <pre
      className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs overflow-x-auto"
      data-testid="raw-json-view"
    >
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}
